import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * Hero — dark, moody post-production room photo on the right, gradient-
 * fading into pure black on the left so the headline sits unobstructed.
 */
export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-[#0a0a0a] pt-28 sm:pt-32 md:pt-36 overflow-hidden"
    >
      {/* Film grain texture (outer) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-[0.25]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.9 0 0 0 0 0.85 0 0 0 0 0.75 0 0 0 0.4 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          backgroundSize: "240px 240px",
        }}
      />

      <div className="relative mx-auto max-w-[1500px] px-4 sm:px-6 md:px-10 pb-14 sm:pb-20">
        <div
          data-testid="hero-frame"
          className="relative overflow-hidden border border-[#1c1c1c] bg-[#050505]"
          style={{
            boxShadow:
              "0 30px 80px rgba(0,0,0,0.75), inset 0 0 0 1px rgba(255,255,255,0.03)",
          }}
        >
          {/* Background image */}
          <img
            src="/hero-room.jpg"
            alt=""
            aria-hidden="true"
            data-testid="hero-bg"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "right center" }}
          />

          {/* Left-to-right darkening gradient — fades to solid black on the left
              so the headline sits on a clean field. */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(5,5,5,1) 0%, rgba(5,5,5,0.97) 22%, rgba(5,5,5,0.85) 38%, rgba(5,5,5,0.55) 52%, rgba(5,5,5,0.15) 68%, rgba(5,5,5,0) 82%)",
            }}
          />
          {/* Extra vignette on very edges */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)",
            }}
          />

          <div className="relative grid grid-cols-1 min-h-[560px] md:min-h-[680px] lg:min-h-[760px]">
            {/* Headline on the left */}
            <div className="relative flex items-center px-6 sm:px-10 md:px-14 lg:px-20 pt-16 pb-14 md:py-0 md:min-h-[680px] lg:min-h-[760px]">
              <div className="max-w-[640px]">
                <h1
                  data-testid="hero-headline"
                  className="font-display font-black text-[32px] xs:text-[36px] sm:text-5xl md:text-6xl lg:text-[62px] xl:text-[70px] leading-[0.95] tracking-tight text-[#f4f1ea]"
                  style={{
                    textShadow:
                      "0 0 22px rgba(255,255,255,0.22), 0 0 42px rgba(255,255,255,0.12), 0 3px 20px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)",
                  }}
                >
                  WE MAKE
                  <br />
                  FILMS{" "}
                  <span
                    className="text-[#e8472b]"
                    style={{
                      textShadow:
                        "0 0 26px rgba(232,71,43,0.55), 0 0 8px rgba(232,71,43,0.35), 0 3px 20px rgba(0,0,0,0.85)",
                    }}
                  >
                    SOUND
                  </span>
                  <br />
                  UNFORGETTABLE
                </h1>

                <a
                  href="#work"
                  data-testid="hero-cta"
                  className="mt-8 inline-flex items-center gap-3 bg-[#f4f1ea] text-[#1a1a1a] font-mono-indie font-semibold text-[12px] sm:text-[13px] tracking-[0.2em] px-6 sm:px-7 py-3 sm:py-4 rounded-full hover:bg-[#e8472b] hover:text-[#f4f1ea] transition-colors"
                  style={{ boxShadow: "0 12px 30px rgba(0,0,0,0.6)" }}
                >
                  HEAR OUR WORK <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Film strip — bottom decorative ribbon */}
      <div
        aria-hidden="true"
        data-testid="film-strip-bottom"
        className="relative h-7 sm:h-8 flex items-center bg-[#0a0a0a] border-y border-[#1f1f1f]"
      >
        <div className="flex w-full justify-around">
          {Array.from({ length: 32 }).map((_, i) => (
            <span
              key={i}
              className="block w-4 h-3 sm:w-5 sm:h-4 bg-[#f4f1ea] rounded-[2px]"
              style={{ opacity: 0.85 }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
